import React from 'react';
import { 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { RunStatus } from '../../types';
import LoadingSpinner from '../common/LoadingSpinner';

interface EnhancedStatusIndicatorProps {
  status: RunStatus;
  isLoading?: boolean;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const EnhancedStatusIndicator: React.FC<EnhancedStatusIndicatorProps> = ({
  status,
  isLoading = false,
  showLabel = true,
  size = 'md',
  className = '',
}) => {
  const getStatusConfig = (status: RunStatus) => {
    switch (status) {
      case 'pending':
        return {
          icon: ClockIcon,
          label: 'Pending',
          color: 'text-gray-500 dark:text-gray-400',
          bgColor: 'bg-gray-100 dark:bg-gray-800',
          borderColor: 'border-gray-300 dark:border-gray-600',
          dotColor: 'bg-gray-400',
        };
      case 'running':
        return {
          icon: PlayIcon,
          label: 'Running',
          color: 'text-primary-600',
          bgColor: 'bg-blue-50 dark:bg-blue-900/20',
          borderColor: 'border-blue-200 dark:border-blue-800',
          dotColor: 'bg-blue-500',
          animate: true,
        };
      case 'completed':
        return {
          icon: CheckCircleIcon,
          label: 'Completed',
          color: 'text-green-600',
          bgColor: 'bg-green-50 dark:bg-green-900/20',
          borderColor: 'border-green-200 dark:border-green-800',
          dotColor: 'bg-green-500',
        };
      case 'failed':
        return {
          icon: ExclamationTriangleIcon,
          label: 'Failed',
          color: 'text-red-600',
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          borderColor: 'border-red-200 dark:border-red-800',
          dotColor: 'bg-red-500',
        };
      case 'paused':
        return {
          icon: PauseIcon,
          label: 'Paused',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
          borderColor: 'border-yellow-200 dark:border-yellow-800',
          dotColor: 'bg-yellow-500',
        };
      case 'stopped':
        return {
          icon: StopIcon,
          label: 'Stopped',
          color: 'text-gray-600 dark:text-gray-400',
          bgColor: 'bg-gray-50 dark:bg-gray-900',
          borderColor: 'border-gray-200 dark:border-gray-700',
          dotColor: 'bg-gray-500 dark:bg-gray-600',
        };
      case 'waiting_for_input':
        return {
          icon: QuestionMarkCircleIcon,
          label: 'Waiting for Input',
          color: 'text-orange-600',
          bgColor: 'bg-orange-50 dark:bg-orange-900/20',
          borderColor: 'border-orange-200 dark:border-orange-800',
          dotColor: 'bg-orange-500',
          animate: true,
        };
      default:
        return {
          icon: QuestionMarkCircleIcon,
          label: 'Unknown',
          color: 'text-gray-500 dark:text-gray-400',
          bgColor: 'bg-gray-100 dark:bg-gray-800',
          borderColor: 'border-gray-300 dark:border-gray-600',
          dotColor: 'bg-gray-400',
        };
    }
  };

  const getSizeClasses = (size: 'sm' | 'md' | 'lg') => {
    switch (size) {
      case 'sm':
        return {
          container: 'px-2 py-1',
          icon: 'w-3 h-3',
          text: 'text-xs',
          dot: 'w-1.5 h-1.5',
        };
      case 'md':
        return {
          container: 'px-3 py-2',
          icon: 'w-4 h-4',
          text: 'text-sm',
          dot: 'w-2 h-2',
        };
      case 'lg':
        return {
          container: 'px-4 py-3',
          icon: 'w-5 h-5',
          text: 'text-base',
          dot: 'w-2.5 h-2.5',
        };
      default:
        return {
          container: 'px-3 py-2',
          icon: 'w-4 h-4',
          text: 'text-sm',
          dot: 'w-2 h-2',
        };
    }
  };

  const config = getStatusConfig(status);
  const sizeClasses = getSizeClasses(size);

  if (isLoading) {
    return (
      <div className={`inline-flex items-center space-x-2 ${sizeClasses.container} bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg ${className}`}>
        <LoadingSpinner size="sm" />
        {showLabel && (
          <span className={`font-medium text-gray-600 dark:text-gray-400 ${sizeClasses.text}`}>
            Loading...
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center space-x-2 ${sizeClasses.container} ${config.bgColor} border ${config.borderColor} rounded-lg ${className}`}>
      {/* Status dot */}
      <div className={`${sizeClasses.dot} ${config.dotColor} rounded-full ${config.animate ? 'animate-pulse' : ''}`}></div>
      
      {/* Status icon */}
      <config.icon className={`${sizeClasses.icon} ${config.color}`} />
      
      {/* Status label */}
      {showLabel && (
        <span className={`font-medium ${config.color} ${sizeClasses.text}`}>
          {config.label}
        </span>
      )}
    </div>
  );
};

export default EnhancedStatusIndicator;